import React, { useRef, useState, useEffect } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Input,
  Space,
  Dropdown,
  Menu,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSpecSections,
  deleteSpecSection,
} from "../services/specSectionsAPI";
import { useAuth } from "../hooks/useAuth";
import CreateSpecSectionModal from "../components/CreateSpecSectionModal";
import UpdateSpecSectionModal from "../components/UpdateSpecSectionModal";
import CopySpecSectionsFromProjectModal from "../components/CopySpecSectionsFromProjectModal";
import Highlighter from "react-highlight-words";
import { exportSpecSectionsToPDF } from "../utils/pdfUtils";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";

const SpecSections = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const projectName = selectedProject.project_name;
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [selectedSpecSection, setSelectedSpecSection] = useState(null);
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [filters, setFilters] = useState({});
  const searchInput = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize('spec_sections'),
    total: 0,
  });
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const {
    data: specSectionsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "specSections",
      projectId,
      pagination.current,
      pagination.pageSize,
      filters,
    ],
    queryFn: () =>
      fetchSpecSections(projectId, pagination.current, pagination.pageSize, filters),
    enabled: !!projectId,
    keepPreviousData: true,
  });


  const deleteMutation = useMutation({
    mutationFn: deleteSpecSection,
    onSuccess: () => {
      queryClient.invalidateQueries(["specSections", projectId]);
      notification.success({ message: "Spec Section deleted successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      console.log(errorMessage);
      notification.error({
        message: "Failed to delete Spec Section",
        description: errorMessage,
      });
    },
  });

  const handleExportPDF = async () => {
    setIsExportingPDF(true);
    try {
      const total = specSectionsData?.total ?? 0;
      if (!total) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });

      const allSpecSectionsData = await fetchSpecSections(
        projectId,
        1,
        total,
        filters,
      );

      const specSections = allSpecSectionsData?.specSections || [];
      if (specSections.length === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      exportSpecSectionsToPDF(specSections, projectName);
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, 'spec_sections');
  };

  if (isError) {
    notification.error({
      message: "Failed to load Specification Sections",
      description: error.message,
    });
  }

  const handleDeleteClick = (specSectionId) => {
    deleteMutation.mutate(specSectionId);
  };

  const handleUpdateClick = (specSection) => {
    setSelectedSpecSection(specSection);
    setIsUpdateModalVisible(true);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    const searchValue = selectedKeys[0] || "";
    setSearchText(searchValue);
    setSearchedColumn(dataIndex);
    setFilters((prev) => ({ ...prev, [dataIndex]: searchValue }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm, dataIndex)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Section Number",
      dataIndex: "section_number",
      key: "section_number",
      ...getColumnSearchProps("section_number"),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ...getColumnSearchProps("title"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="1" onClick={() => handleUpdateClick(record)}>
                Update
              </Menu.Item>
              <Menu.Item key="2">
                <Popconfirm
                  title="Are you sure you want to delete this spec section?"
                  onConfirm={() => handleDeleteClick(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  Delete
                </Popconfirm>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Specification Sections</h1>
      <div className="flex gap-4 mb-4">
        <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
          Create
        </Button>
        <Button type="default" onClick={() => setIsCopyModalVisible(true)}>
          Copy from Project
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportPDF}
          loading={isExportingPDF}
          disabled={isExportingPDF}
        >
          Export PDF
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={specSectionsData?.specSections}
        rowKey="_id"
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: specSectionsData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <CreateSpecSectionModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />
      <UpdateSpecSectionModal
        visible={isUpdateModalVisible}
        onClose={() => setIsUpdateModalVisible(false)}
        specSection={selectedSpecSection}
      />
      <CopySpecSectionsFromProjectModal
        visible={isCopyModalVisible}
        onClose={() => setIsCopyModalVisible(false)}
      />
    </div>
  );
};

export default SpecSections;
